import React, { useEffect, useState, useCallback } from 'react';
import { Target, Save, Copy, LayoutDashboard, SlidersHorizontal, Building2, UserCircle2, Download } from 'lucide-react';
import { toast } from 'sonner';
import { api, fyOptions, nextFy } from './targets/api';
import { TargetOverview } from './targets/TargetOverview';
import { TargetEditor } from './targets/TargetEditor';
import { OrgMasters } from './targets/OrgMasters';
import { MyTargets } from './targets/MyTargets';

const downloadExport = async (fy) => {
  try {
    const res = await fetch(`${window.location.origin}/api/sales-targets/achievement/${fy}/export`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
    if (!res.ok) throw new Error('Export failed');
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url; a.download = `Sales_Roadmap_${fy}.xlsx`; a.click();
    URL.revokeObjectURL(url);
  } catch (err) { toast.error(err.message); }
};

const TABS = [
  { id: 'my', label: 'My Targets', icon: UserCircle2 },
  { id: 'overview', label: 'Target vs Achievement', icon: LayoutDashboard },
  { id: 'set', label: 'Set Targets', icon: SlidersHorizontal },
  { id: 'org', label: 'Companies & BUs', icon: Building2 },
];

export default function SalesTargets() {
  const [tab, setTab] = useState('my');
  const [fy, setFy] = useState('');
  const [plans, setPlans] = useState([]);
  const [currentFy, setCurrentFy] = useState('');
  const [plan, setPlan] = useState(null);
  const [overview, setOverview] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [bus, setBus] = useState([]);
  const [team, setTeam] = useState([]);
  const [dirty, setDirty] = useState(false);

  const loadMasters = useCallback(async () => {
    const [c, b, t, p] = await Promise.all([api('/companies'), api('/business-units'), api('/team'), api('/plans')]);
    setCompanies(c); setBus(b); setTeam(t); setPlans(p.plans); setCurrentFy(p.current_fy);
    setFy(prev => prev || p.current_fy);
  }, []);

  const loadFy = useCallback(async () => {
    if (!fy) return;
    const [p, o] = await Promise.all([api(`/plans/${fy}`), api(`/achievement/${fy}`)]);
    setPlan(p); setOverview(o); setDirty(false);
  }, [fy]);

  useEffect(() => { loadMasters().catch(e => toast.error(e.message)); }, [loadMasters]);
  useEffect(() => { loadFy().catch(e => toast.error(e.message)); }, [loadFy]);

  const editPlan = (p) => { setPlan(p); setDirty(true); };

  const save = async () => {
    try {
      await api(`/plans/${fy}`, { method: 'PUT', body: JSON.stringify(plan) });
      toast.success(`${fy} targets saved`);
      await Promise.all([loadMasters(), loadFy()]);
    } catch (err) { toast.error(err.message); }
  };

  const copy = async () => {
    const to = nextFy(fy);
    const growth = window.prompt(`Copy ${fy} targets to ${to}. Growth % to apply to all amount targets:`, '0');
    if (growth === null) return;
    try {
      await api(`/plans/${fy}/copy`, { method: 'POST', body: JSON.stringify({ to_fy: to, growth_pct: parseFloat(growth) || 0 }) });
      toast.success(`${to} created from ${fy}`);
      await loadMasters();
      setFy(to);
      setTab('set');
    } catch (err) { toast.error(err.message); }
  };

  const reloadAll = async () => { await loadMasters(); await loadFy(); };

  return (
    <div className="space-y-6 max-w-[calc(100vw-3rem)] md:max-w-none" data-testid="sales-targets-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Target className="w-6 h-6 text-red-500" /> Targets &amp; Roadmap</h1>
          <p className="text-slate-500 mt-1 text-sm">Group → Companies → Business Units → Salespeople, measured on won enquiries</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={fy} onChange={e => { if (!dirty || window.confirm('Discard unsaved changes?')) setFy(e.target.value); }} data-testid="targets-fy-select" className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white">
            {currentFy && fyOptions(currentFy, plans.map(p => p.fiscal_year)).map(f => <option key={f} value={f}>{f}{plans.some(p => p.fiscal_year === f) ? '' : ' (no plan)'}</option>)}
          </select>
          <button onClick={() => downloadExport(fy)} disabled={!fy} data-testid="targets-export-btn" className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg hover:bg-slate-50"><Download className="w-4 h-4" /> Export Excel</button>
          {plan?.exists && <button onClick={copy} data-testid="targets-copy-btn" className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg hover:bg-slate-50"><Copy className="w-4 h-4" /> Copy to {fy && nextFy(fy)}</button>}
          {tab === 'set' && <button onClick={save} data-testid="targets-save-btn" className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg text-white ${dirty ? 'bg-red-600 hover:bg-red-700' : 'bg-slate-900 hover:bg-slate-800'}`}><Save className="w-4 h-4" /> Save {fy}{dirty ? ' *' : ''}</button>}
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-slate-200">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`targets-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${tab === t.id ? 'border-red-500 text-red-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {plan && !plan.exists && tab !== 'org' && tab !== 'my' && (
        <p className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="no-plan-hint">No targets saved for {fy} yet. Enter them under Set Targets, or pick a year with a plan and use Copy.</p>
      )}
      {tab === 'my' && fy && <MyTargets fy={fy} team={team} />}
      {tab === 'overview' && overview && <TargetOverview data={overview} />}
      {tab === 'set' && plan && <TargetEditor plan={plan} setPlan={editPlan} companies={companies} bus={bus} team={team} />}
      {tab === 'org' && <OrgMasters companies={companies} bus={bus} reload={reloadAll} />}
    </div>
  );
}
